import { useDeviceType } from '@cian/ui-kit';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { useAllowedCatProfilingPage } from '../../hooks/useAllowedCatProfilingPage';
import { useIsBannerSkipped } from '../../hooks/useIsBannerSkipped';
import { selectMobileBannerCookieState } from '../../selectors/cookies/selectMobileBannerCookieState';
import { selectOnboardingCookieState } from '../../selectors/cookies/selectOnboardingCookieState';
import { selectTestOnboarding } from '../../selectors/onboarding/selectTestOnboarding';
import { useApplicationContext } from '../../utils/applicationContext';

export function useIsOnboardingEnabled(): boolean {
  const { config } = useApplicationContext();
  const testOnboarding = useSelector(selectTestOnboarding);
  const hasOnboardingCookie = useSelector(selectOnboardingCookieState);
  const deviceType = useDeviceType();
  const skippedBanner = useIsBannerSkipped();
  const isWebView = config.get<boolean>('web.isWebView') || false;

  const mobileBannerShown = useSelector(selectMobileBannerCookieState);
  const catProfilingOnboardingCompleted = useSelector(selectOnboardingCookieState);

  const shouldShowFullscreenBanner = useMemo(() => {
    const mobileBannerEnabled = config.get<boolean>('mobileBanner.enabled') ?? false;

    return mobileBannerEnabled && !mobileBannerShown;
  }, [config, mobileBannerShown]);

  const shouldShowCatProfilingBanner = useMemo(() => {
    const profilingBannerEnabled = config.get<boolean>('BX.CatProfiling.Enabled') ?? false;

    return profilingBannerEnabled && !catProfilingOnboardingCompleted;
  }, [config, catProfilingOnboardingCompleted]);

  // переиспользуем хук, т.к. он проверяет разрешенные страницы для профилирования
  // TODO: доработать после раскатки/выпиливания профилирования
  const allowedPage = useAllowedCatProfilingPage();

  const isDisabledOnDesktop = useMemo(
    () => deviceType === 'desktop' && shouldShowCatProfilingBanner,
    [deviceType, shouldShowCatProfilingBanner],
  );

  const isDisabledOnMobile = useMemo(
    () => deviceType === 'phone' && (shouldShowCatProfilingBanner || shouldShowFullscreenBanner),
    [deviceType, shouldShowFullscreenBanner, shouldShowCatProfilingBanner],
  );

  const isDisabled = useMemo(
    () => skippedBanner || isWebView || isDisabledOnDesktop || isDisabledOnMobile,
    [isDisabledOnDesktop, isDisabledOnMobile, isWebView, skippedBanner],
  );

  return useMemo(
    () =>
      (config.get<boolean>('BX.WebOnboarding.Enabled') ?? false) &&
      !isDisabled &&
      allowedPage &&
      (!hasOnboardingCookie || testOnboarding !== null),
    [config, isDisabled, allowedPage, hasOnboardingCookie, testOnboarding],
  );
}
